import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getRotinaConflitos } from '@/services/api/rotinas';
import { DIAS_SEMANA } from './rotinaUtils';

/**
 * As páginas que impedem esta Rotina de entrar no ar, por completo.
 *
 * O 409 de ligar cabe três page_id numa frase; um gestor com 50 páginas em
 * conflito não consegue agir com "e mais 47". Aqui vem a lista inteira, com o
 * nome de cada página — ninguém reconhece a operação por id.
 */
export default function ConflitosDialog({ rotinaId, open, onOpenChange }: {
    rotinaId: number;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}) {
    const q = useQuery({
        queryKey: ['rotina-conflitos', rotinaId],
        queryFn: () => getRotinaConflitos(rotinaId),
        enabled: open,
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-600" />
                        Páginas que já estão em outra Rotina no ar
                    </DialogTitle>
                    <DialogDescription>
                        {q.data
                            ? `"${q.data.nome}" divide ${q.data.total_paginas} página(s) com outra(s) Rotina(s) ligada(s) de ${DIAS_SEMANA[q.data.dia_semana].toLowerCase()}.`
                            : 'Carregando as páginas em conflito…'}
                    </DialogDescription>
                </DialogHeader>

                {q.isLoading && (
                    <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Buscando…
                    </p>
                )}
                {q.isError && (
                    <p className="py-6 text-sm text-destructive">
                        Não foi possível carregar as páginas em conflito.
                    </p>
                )}

                {q.data && q.data.total_paginas === 0 && (
                    <p className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
                        Nenhum conflito agora — esta Rotina pode entrar no ar.
                    </p>
                )}

                {q.data && q.data.rotinas.map((r) => (
                    <div key={r.rotina_id} className="rounded-lg border">
                        <div className="flex items-center justify-between gap-2 border-b bg-muted/40 px-3 py-2">
                            <span className="truncate text-sm font-semibold">{r.nome}</span>
                            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                {r.paginas.length} página(s) em comum
                            </span>
                        </div>
                        <ul className="divide-y text-sm">
                            {r.paginas.map((p) => (
                                <li key={p.page_id} className="flex items-center justify-between gap-3 px-3 py-1.5">
                                    {/* Nome primeiro; o id fica como desempate, porque páginas
                                        de países diferentes repetem o nome com frequência. */}
                                    <span className="truncate">{p.page_name || <em className="text-muted-foreground">sem nome</em>}</span>
                                    <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                                        {p.page_id}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}

                {q.data && q.data.total_paginas > 0 && (
                    <p className="text-xs text-muted-foreground">
                        A mesma página em duas Rotinas do mesmo dia faz o lead receber dois disparos. Tire a
                        página de uma das duas, ou desligue a outra Rotina.
                    </p>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
